import React, { useCallback } from "react";

import cx from "classnames";

import { useID } from "~/lib/hooks";
import { Switch } from "~/ui/legacyFellowIndex";
import Selector, { type SelectorOption, type SingleValue } from "~/ui/selectors/Selector";

import styles from "~/stream/StreamSettings/StreamSettings.module.css";

interface Option extends SelectorOption {
	readonly graphqlValue: string | null;
}

const CarryForwardOptions: readonly Option[] = [
	{ value: "always", label: "Always", graphqlValue: "true", "data-test-id": "carry-forward-always" },
	{ value: "never", label: "Never", graphqlValue: "", "data-test-id": "carry-forward-never" },
	{ value: "ask me", label: "Ask me", graphqlValue: null },
];

export function CarryForwardTalkingPoint({
	title,
	value,
	onSetValue,
}: {
	title: string;
	value: boolean | null;
	onSetValue: (v: string | null) => () => void;
}) {
	return (
		<section className={styles.carryForwardBlock}>
			<CarryForwardBlock title={title} value={value} onSetValue={onSetValue} />
		</section>
	);
}

export function CarryForwardActionItem({
	title,
	value,
	onSetValue,
	carryActionItemsAsLinksValue,
	onSetCarryActionItemsAsLinksValue,
	carryNestedBlocksValue,
	onSetCarryNestedBlocksValue,
}: {
	title: string;
	value: boolean | null;
	onSetValue: (v: string | null) => () => void;
	carryActionItemsAsLinksValue: boolean;
	onSetCarryActionItemsAsLinksValue: (v: string | null) => () => void;
	carryNestedBlocksValue: boolean;
	onSetCarryNestedBlocksValue: (v: string | null) => () => void;
}) {
	const linkedCopiesSwitchID = useID();
	const carryNestedBlocksSwitchID = useID();
	const onLinkedCopiesSwitchChange = useCallback(
		() => onSetCarryActionItemsAsLinksValue(carryActionItemsAsLinksValue ? "" : "true")(),
		[carryActionItemsAsLinksValue, onSetCarryActionItemsAsLinksValue],
	);
	const onCarryNestedBlocksSwitchChange = useCallback(
		() => onSetCarryNestedBlocksValue(carryNestedBlocksValue ? "" : "true")(),
		[carryNestedBlocksValue, onSetCarryNestedBlocksValue],
	);

	return (
		<>
			<section className={styles.carryForwardBlock}>
				<CarryForwardBlock title={title} value={value} onSetValue={onSetValue} />

				<div className={cx(styles.rowContainer, styles.withTopMargin, { [styles.disabled]: value === false })}>
					<label htmlFor={linkedCopiesSwitchID} className={styles.label}>
						Make carried-forward {title} linked copies
					</label>
					<span className={cx(styles.inputContainer, styles.checkboxContainer)}>
						<Switch
							id={linkedCopiesSwitchID}
							checked={carryActionItemsAsLinksValue}
							onCheckedChange={onLinkedCopiesSwitchChange}
							size="medium"
							disabled={value === false}
						/>
					</span>
				</div>
			</section>

			<section className={styles.carryForwardBlock}>
				<div className={cx(styles.rowContainer, styles.withTopMargin)}>
					<label htmlFor={carryNestedBlocksSwitchID} className={styles.label}>
						Include children when carried-forward
					</label>
					<span className={cx(styles.inputContainer, styles.checkboxContainer)}>
						<Switch
							id={carryNestedBlocksSwitchID}
							checked={carryNestedBlocksValue}
							onCheckedChange={onCarryNestedBlocksSwitchChange}
							size="medium"
						/>
					</span>
				</div>
			</section>
		</>
	);
}

function CarryForwardBlock({
	title,
	value,
	onSetValue,
}: {
	title: string;
	value: boolean | null;
	onSetValue: (v: string | null) => () => void;
}) {
	const text = value === true ? "always" : value === false ? "never" : "ask me";
	const onChangeCarryForward = useCallback(
		(value: SingleValue<Option>) => {
			if (value) {
				onSetValue(value.graphqlValue)();
			}
		},
		[onSetValue],
	);

	return (
		<div>
			<h1 className={styles.title}>{title}</h1>
			<div className={styles.rowContainer}>
				<p className={styles.label}>Carry-forward incomplete {title} from the previous note…</p>
				<Selector
					className={styles.inputContainer}
					data-test-id={`carry-forward-${title.split(" ").join("-")}`}
					value={CarryForwardOptions.find(o => o.value === text)}
					options={CarryForwardOptions}
					onChange={onChangeCarryForward}
					width="16rem"
				/>
			</div>
		</div>
	);
}
